import PropTypes from 'prop-types';
import { alpha, Button, Dialog as MUIDialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { useDialog } from 'hooks/useDialog';
import ButtonCloseDialog from 'components/simple/ButtonCloseDialog';

const styles = {
  dialog: ({ breakpoints }) => ({
    [breakpoints.up('md')]: { padding: 3 },
  }),
  dialogPaper: ({ breakpoints, palette }) => ({
    margin: 3,
    borderRadius: '10px',
    boxShadow: `0 4px 4px 0 ${alpha(palette.common.black, 0.25)}`,
    width: 'calc(100% - 48px)',

    [breakpoints.up('sm')]: {
      maxWidth: 600,
      width: 'calc(100% - 64px)',
    },

    [breakpoints.up('md')]: {
      maxWidth: 720,
    },

    [breakpoints.up('lg')]: {
      maxWidth: 840,
    },
  }),
  title: ({ breakpoints }) => ({
    padding: 3,
    paddingRight: 8,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
    marginBottom: 0,

    [breakpoints.up('md')]: {
      marginBottom: 2,
    },
  }),
  actions: ({ breakpoints }) => ({
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: 3,
    gap: 1,
    width: '100%',
    alignItems: 'stretch',

    [breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
  }),
  content: ({ breakpoints }) => ({
    paddingBottom: 0,
    [breakpoints.up('md')]: {
      paddingBottom: 2.5,
    },
  }),
};

export const Dialog = ({
  id,
  title = '',
  children,
  open = true,
  onConfirm = null,
  onClose = null,
  onCancel = null,
  confirmLabel = '',
  cancelLabel = '',
  customStyles: { contentStyles, paperStyles, titleStyles, actionsStyles } = {
    contentStyles: {},
    paperStyles: {},
    titleStyles: {},
    actionsStyles: {},
  },
  withCustomContent = false,
  isDisabledConfirm = false,
  isLoading = false,
}) => {
  const { closeDialog } = useDialog();
  const { isMdDown: isMobile } = useBreakpointFlags();

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    if (onClose) {
      onClose();
    } else {
      onCancel?.();
    }

    closeDialog();
  };

  const handleCancel = () => {
    onCancel?.();
    closeDialog();
  };

  return (
    <MUIDialog
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      sx={styles.dialog}
      slotProps={{ paper: { sx: [styles.dialogPaper, paperStyles] } }}
    >
      <ButtonCloseDialog onClick={handleClose} />

      <DialogTitle id={`${id}-title`} variant={isMobile ? 'subtitle1' : 'h2'} sx={[styles.title, titleStyles]}>
        {title}
      </DialogTitle>

      {withCustomContent ? (
        children
      ) : (
        <>
          <DialogContent sx={[styles.content, contentStyles]}>{children}</DialogContent>

          <DialogActions sx={[styles.actions, actionsStyles]} disableSpacing>
            {cancelLabel && (
              <Button onClick={handleCancel} color="secondary" disabled={isLoading}>
                {cancelLabel}
              </Button>
            )}

            {onConfirm && confirmLabel && (
              <Button onClick={onConfirm} disabled={isDisabledConfirm} loading={isLoading}>
                {confirmLabel}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </MUIDialog>
  );
};

Dialog.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  isDisabledConfirm: PropTypes.bool,
  isLoading: PropTypes.bool,
  customStyles: PropTypes.shape({
    contentStyles: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
    paperStyles: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
    titleStyles: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
    actionsStyles: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  }),
  withCustomContent: PropTypes.bool,
};
